import { useState, useMemo } from 'react';
import { verifiedImagesClient } from '../../../infrastructure/VerifiedImagesClient';
import { mediaClient } from '../../../infrastructure/MediaClient';
import { convertToCloudFrontUrl } from '../../../pages/sourcing/matched/[cpProductId]/image-editor/utils/imageUtils';
import type { CpGpMatchedProduct } from '../../../models/Coupang';
import type { SkinSeoulAdminProduct } from '../../../models/ProductTypes';
import type { ImageSelection } from './useImageEditor';

interface UseImagePublishParams {
  matchedProduct?: CpGpMatchedProduct;
  imageSelection: ImageSelection;
  refetchProductsBySku?: (() => Promise<any>) | undefined;
  refetchVerifiedImages?: (() => Promise<any>) | undefined;
  productsBySku?: SkinSeoulAdminProduct[] | undefined;
  productKeyToSkuMap?: Record<string, string> | undefined;
  editMode?: 'thu' | 'pdp';
}

export const useImagePublish = ({
  matchedProduct,
  imageSelection,
  refetchProductsBySku,
  refetchVerifiedImages,
  productsBySku = [],
  productKeyToSkuMap = {},
  editMode = 'thu',
}: UseImagePublishParams) => {
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishMessage, setPublishMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [recentlyPublishedSkus, setRecentlyPublishedSkus] = useState<Set<string>>(new Set());
  const [publishingSkus, setPublishingSkus] = useState<Set<string>>(new Set());

  // Extract SKUs from productsBySku
  const skus = useMemo(() => {
    return productsBySku.map((p) => p.sku).filter((sku): sku is string => Boolean(sku));
  }, [productsBySku]);

  // Publish to WordPress (calls mediaClient APIs)
  const publishToWordPress = async () => {
    if (!matchedProduct) {
      return;
    }

    setPublishLoading(true);
    setPublishMessage(null);
    setRecentlyPublishedSkus(new Set());

    try {
      const publishResults: string[] = [];
      const publishErrors: string[] = [];
      const successfulSkus = new Set<string>();
      const allSkusToPublish = new Set<string>();

      // Collect all SKUs that will be published
      Object.entries(imageSelection.thumbnails).forEach(([productKey, imageUrl]) => {
        if (imageUrl) {
          const sku = productKeyToSkuMap[productKey];
          if (sku) {
            allSkusToPublish.add(sku);
          }
        }
      });
      skus.forEach((sku) => allSkusToPublish.add(sku));

      // Set publishing state for all SKUs
      setPublishingSkus(allSkusToPublish);

      // THU Mode: Publish featured thumbnails and gallery images
      if (editMode === 'thu') {
        // Publish featured thumbnails - use featuredThumbnailUrl from RestructuredProduct
        for (const [productKey, imageUrl] of Object.entries(imageSelection.thumbnails)) {
          if (!imageUrl) {
            continue;
          }

          // Get SKU from the productKey mapping
          const sku = productKeyToSkuMap[productKey];
          if (!sku) {
            continue;
          }

          // Extract cpItemId from productKey
          const cpItemId = productKey.split('_')[0];

          // Use the imageUrl from imageSelection (UI selected image), not from restructuredProducts
          // because restructuredProducts might not be refetched yet after updateRestructuredProducts API call
          const featuredThumbnailUrl = imageUrl;

          console.info(`📤 Publishing featured thumbnail for SKU ${sku} (cpItemId: ${cpItemId}):`, {
            sku,
            cpItemId,
            featuredThumbnailUrl,
          });

          try {
            const response = await mediaClient.publishFeaturedThumbnail({
              sku,
              imageUrl: featuredThumbnailUrl,
            });

            if (response.success) {
              publishResults.push(`Thumbnail for ${sku}`);
              successfulSkus.add(sku);
            } else {
              publishErrors.push(`Thumbnail for ${sku}: ${response.message}`);
            }
          } catch (err) {
            publishErrors.push(`Thumbnail for ${sku}: ${err instanceof Error ? err.message : 'Unknown error'}`);
          }
        }

        // Publish gallery images for each SKU
        if (imageSelection.gallery.length > 0) {
          for (const sku of skus) {
            try {
              const response = await mediaClient.publishGalleryImages({
                sku,
                toAdd: imageSelection.gallery,
              });

              if (response.success) {
                publishResults.push(`Gallery for ${sku}`);
                successfulSkus.add(sku);
              } else {
                publishErrors.push(`Gallery for ${sku}: ${response.message}`);
              }
            } catch (err) {
              publishErrors.push(`Gallery for ${sku}: ${err instanceof Error ? err.message : 'Unknown error'}`);
            }
          }
        }
      }

      // PDP Mode: Publish PDP images (LearnMoreUrl) - images already saved to S3 with /en/ prefix
      if (editMode === 'pdp') {
        if (imageSelection.pdpImages.length > 0) {
          for (const sku of skus) {
            try {
              // Publish learn more images using already saved S3 URLs
              const response = await mediaClient.publishLearnMoreImages({
                sku,
                toAdd: imageSelection.pdpImages,
              });

              if (response.success) {
                publishResults.push(`Learn More images for ${sku}`);
                successfulSkus.add(sku);
              } else {
                publishErrors.push(`Learn More for ${sku}: ${response.message}`);
              }
            } catch (err) {
              publishErrors.push(`Learn More for ${sku}: ${err instanceof Error ? err.message : 'Unknown error'}`);
            }
          }
        } else {
          publishErrors.push('No PDP images selected to publish');
        }
      }

      // Clear publishing state
      setPublishingSkus(new Set());

      // Refetch productsBySku to show updated WP data
      if (refetchProductsBySku) {
        await refetchProductsBySku();
      }

      // Update recently published SKUs (show success state)
      setRecentlyPublishedSkus(successfulSkus);

      // Auto-clear the success overlay after 2 seconds
      setTimeout(() => {
        setRecentlyPublishedSkus(new Set());
      }, 2000);

      const successMessage = publishResults.length > 0 ? `Successfully published to WordPress!` : 'No items to publish';

      const errorMessage = publishErrors.length > 0 ? ` (Some errors: ${publishErrors.slice(0, 2).join(', ')})` : '';

      setPublishMessage({
        type: publishErrors.length > 0 ? 'error' : 'success',
        text: successMessage + errorMessage,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setPublishMessage({
        type: 'error',
        text: `Failed to publish: ${errorMessage}`,
      });
      setPublishingSkus(new Set());
    } finally {
      setPublishLoading(false);
    }
  };

  const deletePdpImage = async (url: string) => {
    if (!matchedProduct) {
      return;
    }

    const urlToDelete = convertToCloudFrontUrl(url);

    if (!urlToDelete.includes('/en/')) {
      alert('Can only delete translated PDP images (images with /en/ in path)');
      return;
    }

    try {
      const result = await verifiedImagesClient.deleteVerifiedImages({
        gpProductId: matchedProduct.gpProductId,
        cpProductId: matchedProduct.cpProductId,
        cpItemId: matchedProduct.cpItemId,
        imageUrls: [urlToDelete],
      });

      if (result.success) {
        if (refetchVerifiedImages) {
          await refetchVerifiedImages();
        }
      } else {
        alert(`Failed to delete image: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      alert('Failed to delete image. Please try again.');
    }
  };

  return {
    publishToWordPress,
    deletePdpImage,
    publishLoading,
    publishMessage,
    recentlyPublishedSkus,
    publishingSkus,
  };
};
